<style lang="scss" scoped>
.checkout {
    max-width: 1840px;

    .tooltip {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        font-size: 8px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        color: #fff;
        margin-left: 4px;
        cursor: default;

        @apply bg-green;
    }
}
</style>

<template>
    <section class="checkout xl:px-80 lg:px-32 md:px-40 px-20">
        <div class="bg-blue rounded-xs text-14 flex items-center w-full px-16 py-16 mb-20 font-medium leading-snug text-white" v-if="message != null && message != ''">{{ message }}</div>

        <ValidationObserver v-slot="{ invalid }" tag="div" :slim="true" ref="observer">
            <form @submit.prevent="submit" :key="_uid">
                <el-error :error="server_error"></el-error>
                <div class="-mx-30 lg:-mx-16 xl:-mx-30 flex flex-wrap">
                    <div class="px-30 lg:px-16 xl:px-30 lg:w-1/3 w-full">
                        <h5 class="mb-16">{{ translations.checkout_data.personal_info }}</h5>

                        <el-input v-model="formData.client.email" name="email" rules="required|email" :label="translations.checkout_data.email" class="mb-16"></el-input>
                        <el-input
                            v-model="formData.client.phone"
                            name="phone"
                            :rules="{
                                required: true,
                                regex: /((?:\+|00)[17](?: |\-)?|(?:\+|00)[1-9]\d{0,2}(?: |\-)?|(?:\+|00)1\-\d{3}(?: |\-)?)?(0\d|\([0-9]{3}\)|[1-9]{0,3})(?:((?: |\-)[0-9]{2}){4}|((?:[0-9]{2}){4})|((?: |\-)[0-9]{3}(?: |\-)[0-9]{4})|([0-9]{7}))/,
                            }"
                            :label="translations.checkout_data.phone"
                            class="mb-16"
                        ></el-input>

                        <div class="flex mb-40">
                            <div class="flex items-center mr-32">
                                <input type="radio" v-model="formData.client.type" id="particulier" value="private" />
                                <label for="particulier" class="ml-8">{{ translations.checkout_data.private }}</label>
                            </div>
                            <div class="flex items-center">
                                <input type="radio" v-model="formData.client.type" id="bedrijf" value="company" />
                                <label for="bedrijf" class="ml-8">{{ translations.checkout_data.company }}</label>
                            </div>
                        </div>

                        <template v-if="formData.client.type === 'company'">
                            <el-input v-model="formData.client.company_name" name="company" rules="required" :label="translations.checkout_data.company_name" class="mb-16"></el-input>
                            <el-input
                                v-model="formData.client.vat_number"
                                @input="vatUpper"
                                :rules="{
                                    required: true,
                                    regex: /^((AT)U[0-9]{8}|(BE)[0-9]{10}|(BG)[0-9]{9,10}|(HR)[0-9]{11}|(CY)[0-9]{8}L|(CZ)[0-9]{8,10}|(DE)[0-9]{9}|(DK)[0-9]{8}|(EE)[0-9]{9}|(EL|GR)[0-9]{9}|ES[A-Z][0-9]{7}(?:[0-9]|[A-Z])|(FI)[0-9]{8}|(FR)[0-9A-Z]{2}[0-9]{9}|(GB)([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})|(HU)[0-9]{8}|(IE)[0-9]S[0-9]{5}L|(IT)[0-9]{11}|(LT)([0-9]{9}|[0-9]{12})|(LU)[0-9]{8}|(LV)[0-9]{11}|(MT)[0-9]{8}|(NL)[0-9]{9}B[0-9]{2}|(PL)[0-9]{10}|(PT)[0-9]{9}|(RO)[0-9]{2,10}|(SE)[0-9]{12}|(SI)[0-9]{8}|(SK)[0-9]{10})$/,
                                }"
                                name="vatNumber"
                                :label="translations.checkout_data.vat_number"
                                placeholder="BE0123456789"
                                class="mb-40"
                            ></el-input>
                        </template>

                        <el-input v-model="formData.client.first_name" name="firstName" rules="required" :label="translations.checkout_data.first_name" class="mb-16"></el-input>
                        <el-input v-model="formData.client.last_name" name="name" rules="required" :label="translations.checkout_data.name" class="mb-40"></el-input>

                        <h5 class="mb-16">{{ translations.checkout_data.payment_address }}</h5>
                        <el-input v-model="formData.billing_address.street" name="payment_street" rules="required" :label="translations.checkout_data.street" class="mb-16"></el-input>
                        <div class="flex -mx-8">
                            <div class="w-1/2 px-8">
                                <el-input v-model="formData.billing_address.number" name="payment_number" rules="required" :label="translations.checkout_data.number" class="mb-16"></el-input>
                            </div>
                            <div class="w-1/2 px-8">
                                <el-input v-model="formData.billing_address.addition" name="payment_addition" :label="translations.checkout_data.addition" class="mb-16"></el-input>
                            </div>
                        </div>
                        <div class="flex -mx-8">
                            <div class="w-1/3 px-8">
                                <el-input v-model="formData.billing_address.zip" @change="calculateShippingCosts()" name="payment_postalCode" rules="required" :label="translations.checkout_data.postal_code" class="mb-16"></el-input>
                            </div>
                            <div class="w-2/3 px-8">
                                <el-input v-model="formData.billing_address.city" name="payment_place" rules="required" :label="translations.checkout_data.location" class="mb-16"></el-input>
                            </div>
                        </div>
                        <label class="text-14 inline-block mb-16 font-medium" style="color: #6c6c70">* {{ translations.checkout_data.country }}</label>
                        <v-select
                            class="mb-16"
                            @input="
                                (val) => {
                                    formData.billing_address.country = translations.checkout_data.countries.find((c) => c.country_name === val).country_code;
                                    calculateShippingCosts();
                                }
                            "
                            :clearable="false"
                            :options="availableCountries"
                            v-model="billing_address_country"
                        ></v-select>

                        <el-input v-if="shippingAllowed" v-model="formData.shipping_same" @input="calculateShippingCosts()" name="shipping_same" type="checkbox" class="mb-40" :label="translations.checkout_data.same_address"></el-input>

                        <template v-if="!formData.shipping_same">
                            <h5 class="mb-16">Afleveradres</h5>
                            <el-input v-model="formData.shipping_address.street" name="delivery_street" rules="required" :label="translations.checkout_data.street" class="mb-16"></el-input>
                            <div class="flex -mx-8">
                                <div class="w-1/2 px-8">
                                    <el-input v-model="formData.shipping_address.number" name="delivery_number" rules="required" :label="translations.checkout_data.number" class="mb-16"></el-input>
                                </div>
                                <div class="w-1/2 px-8">
                                    <el-input v-model="formData.shipping_address.addition" name="delivery_addition" :label="translations.checkout_data.addition" class="mb-16"></el-input>
                                </div>
                            </div>
                            <div class="flex -mx-8">
                                <div class="w-1/3 px-8">
                                    <el-input v-model="formData.shipping_address.zip" @change="calculateShippingCosts()" name="delivery_postalCode" rules="required" :label="translations.checkout_data.postal_code" class="mb-16"></el-input>
                                </div>
                                <div class="w-2/3 px-8">
                                    <el-input v-model="formData.shipping_address.city" name="delivery_place" rules="required" :label="translations.checkout_data.location" class="mb-16"></el-input>
                                </div>
                            </div>
                            <label class="text-14 inline-block mb-16 font-medium" style="color: #6c6c70">* {{ translations.checkout_data.location }}</label>
                            <v-select
                                class="mb-40"
                                @input="
                                    (val) => {
                                        formData.shipping_address.country = translations.checkout_data.countries.find((c) => c.country_name === val).country_code;
                                        calculateShippingCosts();
                                    }
                                "
                                :clearable="false"
                                :options="availableCountries"
                                v-model="shipping_address_country"
                            ></v-select>
                        </template>

                        <el-input class="lg:mb-0 mb-40" v-model="formData.remark" name="remark" type="textarea" :label="translations.checkout_data.message"></el-input>
                    </div>
                    <div class="px-30 lg:px-16 xl:px-30 lg:w-1/3 w-full">
                        <h5 class="mb-16">{{ translations.checkout_data.shipment }}</h5>
                        <p class="text-14 mb-16 font-medium text-gray-500">{{ translations.checkout_data.only_pickup_message }}</p>
                        <div class="rounded-xs p-24 mb-16 bg-gray-100" @click="clickChildInput">
                            <div class="flex items-center">
                                <input type="radio" v-model="formData.shipping_method" id="zelf-afhalen" value="pick_up" />
                                <label for="zelf-afhalen" class="ml-8">{{ translations.checkout_data.pickup }}</label>
                            </div>
                        </div>
                        <div v-if="shippingAllowed" class="rounded-xs p-24 mb-40 bg-gray-100" @click="clickChildInput">
                            <div class="flex justify-between mb-0">
                                <div class="flex items-center">
                                    <input type="radio" v-model="formData.shipping_method" :disabled="!shippingAllowed" id="verzending" value="delivery" />
                                    <label for="verzending" class="ml-8">{{ translations.checkout_data.shipment_label }}</label>
                                    <tooltip :text="translations.checkout_data.shipment_tooltip"></tooltip>
                                </div>
                                <span class="text-gray-500" v-if="priceAtRequest">Op aanvraag</span>
                                <span class="text-gray-500" v-else>&euro; {{ $store.getters['pricesIncludingVat'] ? shippingCostsVatIncl : shippingCosts }}</span>
                            </div>
                            <div v-if="false" class="flex justify-between pl-24">
                                <div class="flex items-center">
                                    <input type="checkbox" v-model="formData.forklift_required" :disabled="!shippingAllowed || formData.shipping_method !== 'delivery'" id="kooiaap" />
                                    <label for="kooiaap" class="ml-8">{{ translations.checkout_data.forklift_necessary }}</label>
                                </div>
                                <span class="text-gray-500">&euro; 60</span>
                            </div>
                        </div>

                        <h5 class="mb-24">{{ translations.checkout_data.payment_options }}</h5>
                        <div class="rounded-xs p-24 mb-16 bg-gray-100" @click="clickChildInput">
                            <div class="flex items-center">
                                <input type="radio" v-model="formData.payment_method" id="bancontact" value="bancontact" />
                                <span v-html="svg('bancontact')" class="ml-16"></span>
                                <label for="bancontact" class="ml-16">Bancontact</label>
                            </div>
                        </div>
                        <div class="rounded-xs p-24 mb-16 bg-gray-100" @click="clickChildInput">
                            <div class="flex items-center">
                                <input type="radio" v-model="formData.payment_method" id="ideal" value="ideal" />
                                <span v-html="svg('ideal')" class="ml-16"></span>
                                <label for="ideal" class="ml-16">iDEAL</label>
                            </div>
                        </div>
                        <div class="rounded-xs p-24 mb-16 bg-gray-100" @click="clickChildInput">
                            <div class="flex items-center">
                                <input type="radio" v-model="formData.payment_method" id="visa" value="creditcard" />
                                <div class="flex flex-col ml-16">
                                    <span v-html="svg('visa')" class="svg-w-36 mb-4"></span>
                                    <span v-html="svg('mastercard')" class="svg-w-36"></span>
                                </div>
                                <label for="visa" class="ml-16">
                                    <div>Kredietkaart</div>
                                    <div v-if="creditcard_fee">
                                        <small class="text-gray-500">Transactiekost: +2% (€{{ formatCurrency(creditcard_fee) }}) - Directe verzending</small>
                                    </div>
                                </label>
                            </div>
                        </div>
                        <div v-if="$store.getters['isReseller']" class="rounded-xs p-24 mb-16 bg-gray-100" @click="clickChildInput">
                            <div class="flex items-center">
                                <input type="radio" v-model="formData.payment_method" id="banktransfer" value="banktransfer" />
                                <span v-html="svg('mastercard')" class="ml-16"></span>
                                <label for="banktransfer" class="ml-16">Bankoverschrijving</label>
                            </div>
                        </div>
                        <div v-if="$store.getters['isReseller'] && $store.getters['payByInvoice']" class="rounded-xs p-24 mb-16 bg-gray-100" @click="clickChildInput">
                            <div class="flex items-center">
                                <input type="radio" v-model="formData.payment_method" id="invoice" value="invoice" />
                                <span class="ml-10"
                                    ><svg class="w-32 h-32" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg
                                ></span>
                                <label for="invoice" class="ml-10">Factuur</label>
                            </div>
                        </div>
                    </div>
                    <div class="px-30 lg:px-16 xl:px-30 lg:w-1/3 w-full">
                        <div class="rounded-xs lg:p-32 p-24 mb-24 bg-gray-100">
                            <h5 class="mb-24">{{ translations.checkout_data.samenvatting }}</h5>

                            <div class="flex mb-8" v-for="product in basket.products" :key="product.id">
                                <span class="inline-block w-40 pr-16 text-black">{{ product.amount }}x</span>
                                <p class="text-16 whitespace-nowrap overflow-ellipsis mb-8 overflow-hidden text-black" v-html="product.productData.name"></p>
                                <span class="flex-shrink-0 pl-16 ml-auto text-gray-500">&euro; {{ formatCurrency(getVariantPrice(product)) }}</span>
                            </div>

                            <div class="flex justify-between mt-24 mb-8" v-if="formData.shipping_method == 'delivery'">
                                <span class="text-black">{{ translations.checkout_data.shipment }}</span>
                                <span class="text-gray-500" v-if="priceAtRequest">Op aanvraag</span>
                                <span class="text-gray-500" v-else>&euro; {{ formatCurrency(shippingCost) }}</span>
                            </div>
                            <div class="flex justify-between mb-8" v-if="formData.forklift_required && formData.shipping_method == 'delivery'">
                                <span class="text-black">Kooiaap</span>
                                <span class="text-gray-500">&euro; {{ formatCurrency(forkliftCost) }}</span>
                            </div>

                            <hr class="my-24 border-gray-500" />

                            <div class="flex justify-between mb-8">
                                <span class="text-black">{{ translations.checkout_data.subtotal }}</span>
                                <span class="text-gray-500">&euro; {{ subTotalPlusShipping }}</span>
                            </div>
                            <div class="flex justify-between mb-8">
                                <span class="text-black">{{ translations.checkout_data.vat }}</span>
                                <span class="text-gray-500">&euro; {{ vatPlusShippingVat }}</span>
                            </div>
                            <div v-if="formData.payment_method === 'creditcard'" class="flex justify-between mb-8">
                                <span class="text-black">Transactiekost</span>
                                <span class="text-gray-500">&euro; {{ formatCurrency(creditcard_fee) }}</span>
                            </div>
                            <div class="flex justify-between mb-24">
                                <span class="text-black">{{ translations.checkout_data.shipment }}</span>
                                <span class="text-gray-500">{{ formData.shipping_method === 'delivery' ? 'Verzending' : 'Zelf afhalen' }}</span>
                            </div>
                            <div class="flex justify-between mb-24">
                                <span class="text-20 font-bold text-black">{{ translations.checkout_data.total }}</span>
                                <span class="text-gray-500">&euro; {{ absoluteTotal }}</span>
                            </div>
                        </div>

                        <div class="text-center">
                            <p class="mb-24" v-html="translations.checkout_data.terms"></p>
                            <tooltip v-if="$store.getters['basketErrors'].length && $store.getters['basketErrors'].find((e) => ['out_of_stock', 'not_enough_stock'].includes(e.issue))" :text="translations.shopping_basket.shopping_basket_stock_error" class="w-full">
                                <div class="btn btn-rounded w-full mb-24 text-center text-white border-none pointer-events-none" style="background-color: rgba(35, 179, 47, 0.6)">{{ translations.checkout_data.submit_button }}<span class="bg-red text-12 inline-flex items-center justify-center w-16 h-16 ml-16 font-bold text-white rounded-full">!</span></div>
                            </tooltip>
                            <tooltip v-else-if="$store.getters['basketErrors'].length && $store.getters['basketErrors'].find((e) => e.issue === 'order_amount_too_low')" :text="translations.shopping_basket.order_amount_too_low.replace(/\{{(.+?)\}}/g, $store.getters['customer'].min_order_amount)" class="w-full">
                                <div class="btn btn-rounded w-full mb-24 text-center text-white border-none pointer-events-none" style="background-color: rgba(35, 179, 47, 0.6)">{{ translations.checkout_data.submit_button }}<span class="bg-red text-12 inline-flex items-center justify-center w-16 h-16 ml-16 font-bold text-white rounded-full">!</span></div>
                            </tooltip>
                            <el-button v-else class="btn btn-green btn-rounded w-full mb-24" @click="submit" :no-arrow="true" :loading="loading" :success="success" :loadingLabel="translations.checkout_data.submit_button" :successLabel="translations.checkout_data.submit_button" :defaultLabel="translations.checkout_data.submit_button" :disabled="invalid"></el-button>
                            <p>
                                <a href="/contact" target="_blank" class="hover:border-transparent leading-tight transition-colors border-b border-black">{{ translations.checkout_data.help_label }}</a>
                            </p>
                        </div>
                    </div>
                </div>
            </form>
        </ValidationObserver>
    </section>
</template>

<script>
import shoppingBasket from '@mixins/components/shopping-basket';
import { mapGetters } from 'vuex';

import hasForm from '@mixins/components/has-form';
import { ValidationObserver, validate } from 'vee-validate';
import { extend } from 'vee-validate';
import * as rules from 'vee-validate/dist/rules';
import nl from 'vee-validate/dist/locale/nl.json';
for (let rule in rules) {
    extend(rule, {
        ...rules[rule],
        message: nl.messages[rule],
    });
}

export default {
    name: 'Ui.Checkout',

    mixins: [shoppingBasket, hasForm],

    components: { ValidationObserver },

    data() {
        return {
            message: null,
            availableCountries: this.translations.checkout_data.countries.map((x) => x.country_name),
            shippingAllowed: false,
            shippingCosts: 0,
            shippingCostsVatIncl: 0,
            priceAtRequest: false,
            billing_address_country: this.translations.checkout_data.countries[0].country_name,
            shipping_address_country: this.translations.checkout_data.countries[0].country_name,
            formData: {
                client: {
                    type: '',
                    first_name: '',
                    last_name: '',
                    company_name: '',
                    vat_number: '',
                    email: '',
                    phone: '',
                },
                shipping_same: true,
                billing_address: {
                    street: '',
                    number: '',
                    addition: '',
                    zip: '',
                    city: '',
                    country: this.translations.checkout_data.countries[0].country_code,
                },
                shipping_address: {
                    street: '',
                    number: '',
                    addition: '',
                    zip: '',
                    city: '',
                    country: this.translations.checkout_data.countries[0].country_code,
                },
                remark: '',
                shipping_method: 'pick_up',
                payment_method: 'bancontact',
                forklift_required: false,
                redirect_url: `${window.location.protocol}//${window.location.host}/betaling?{id}`,
            },
        };
    },

    mounted() {
        const self = this;
        this.readCheckoutCookie();

        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get('status');

        if (status != null) {
            this.message = this.$t(`forms.checkout.messages.${status}`);
        }

        this.$store.dispatch('syncBasketProducts').then(() => {
            this.calculateShippingCosts();
        });
    },

    methods: {
        clickChildInput(event) {
            const input = event.target.getElementsByTagName('input')[0];
            if (input) {
                input.click();
            }
        },

        updateCheckoutCookie() {
            const checkoutInfo = {
                client: this.formData.client,
                shipping_same: this.formData.shipping_same,
                billing_address: this.formData.billing_address,
                shipping_address: this.formData.shipping_address,
                payment_method: this.formData.payment_method,
                remark: this.formData.remark,
                shipping_method: this.formData.shipping_method,
                forklift_required: this.formData.forklift_required,
            };

            this.$cookies.set('checkout_info', checkoutInfo, '30d');
        },

        readCheckoutCookie() {
            const checkoutInfo = this.$cookies.get('checkout_info');

            if (checkoutInfo) {
                this.formData.client = checkoutInfo.client;
                this.formData.shipping_same = checkoutInfo.shipping_same;
                this.formData.billing_address = checkoutInfo.billing_address;
                this.formData.shipping_address = checkoutInfo.shipping_address;
                this.formData.payment_method = checkoutInfo.payment_method;
                this.formData.remark = checkoutInfo.remark ? checkoutInfo.remark : this.formData.remark;
                this.formData.shipping_method = checkoutInfo.shipping_method ? checkoutInfo.shipping_method : this.formData.shipping_method;
                this.formData.forklift_required = checkoutInfo.forklift_required ? checkoutInfo.forklift_required : this.formData.forklift_required;
            }
        },

        calculateShippingCosts() {
            const self = this;

            const variants = self.basket.products.map((p) => {
                return {
                    id: p.variant.id,
                    quantity: parseInt(p.amount),
                };
            });

            let zip = self.formData.shipping_same ? self.formData.billing_address.zip : self.formData.shipping_address.zip;

            if (zip === '') {
                zip = 'NO_ZIP';
            }

            const payload = {
                variants: variants,
                address: {
                    zip: zip,
                    country: self.formData.shipping_same ? self.formData.billing_address.country : self.formData.shipping_address.country,
                },
            };

            axios.post('/api/order-shipping-costs', payload).then((response) => {
                self.shippingAllowed = response.data.shipping_allowed;
                self.shippingCosts = response.data.shipping_costs;
                self.shippingCostsVatIncl = response.data.shipping_costs_vat_incl;
                self.priceAtRequest = response.data.price_at_request;
            });
        },

        vatUpper() {
            this.formData.client.vat_number = this.formData.client.vat_number.toUpperCase().replace('.', '');
        },

        submit() {
            this.beforeSubmit();

            const params = this.formData;

            if (params.billing_address.addition === '') {
                delete params.billing_address.addition;
            }

            if (params.shipping_address.addition === '') {
                delete params.shipping_address.addition;
            }

            params.variants = [];
            this.basket.products.forEach((product) => {
                let variant = { quantity: parseInt(product.amount), id: product.variant.id };
                params.variants.push(variant);
            });

            axios
                .post('/api/order', params)
                .then((response) => {
                    if (response && response.data && response.data.data && response.data.data.redirect_url) {
                        window.location.href = response.data.data.redirect_url;
                    }
                })
                .catch((e) => this.handleError(e.response.data));
        },
    },

    computed: {
        ...mapGetters(['customer']),
        shippingCost() {
            if (this.formData.shipping_method === 'pick_up') {
                return 0;
            }
            if (this.formData.shipping_method === 'delivery') {
                return this.priceAtRequest ? 0 : this.shippingCosts;
            }
        },

        shippingCostVatIncl() {
            if (this.formData.shipping_method === 'pick_up') {
                return 0;
            }
            if (this.formData.shipping_method === 'delivery') {
                return this.priceAtRequest ? 0 : this.shippingCostsVatIncl;
            }
        },

        shipping_method() {
            return this.formData.shipping_method;
        },

        creditcard_fee() {
            if (!this.totalPrice) {
                return 0;
            }

            return parseFloat(this.totalPrice.replace(',', '.')) * 0.02;
        },

        forkliftCost() {
            return this.formData.forklift_required && this.formData.shipping_method === 'delivery' ? 60 : 0;
        },

        vatPlusShippingVat() {
            return this.formatCurrency(this.$store.getters['vat'] + (this.formData.shipping_method === 'delivery' ? this.shippingCostsVatIncl - this.shippingCosts : 0));
        },

        subTotalPlusShipping() {
            let subTotal = this.$store.getters['subTotal'] + (this.formData.shipping_method === 'delivery' ? this.shippingCosts : 0) + this.forkliftCost;

            return this.formatCurrency(subTotal);
        },

        absoluteTotal() {
            let creditcard_fee = 0;

            if (this.formData.payment_method === 'creditcard') {
                creditcard_fee = this.creditcard_fee;
            }

            return this.formatCurrency(this.$store.getters['totalPrice'] + this.shippingCostVatIncl + this.forkliftCost + creditcard_fee);
        },
    },

    watch: {
        shipping_method() {
            if (this.formData.shipping_method === 'pick_up') {
                this.formData.forklift_required = false;
            }
        },

        formData: {
            deep: true,
            handler: function (formData) {
                this.updateCheckoutCookie();
            },
        },

        customer: {
            immediate: true,
            deep: true,
            handler: function (customer) {
                if (customer.email) {
                    this.formData.client.email = customer.email;
                }
            },
        },
    },
};
</script>
